import { registerComponent, getStoreData } from "helpers";
import WYATTElement from "wyatt";

// Stores flagged as coming soon are omitted from the pickup markup. The
// metaobject boolean can serialize as a raw boolean or as a value object.
function isComingSoon(store) {
  const value = store?.coming_soon?.value ?? store?.coming_soon;

  return value === true || value === "true";
}

registerComponent(
  "store-pickup-form",
  class StorePickupForm extends WYATTElement {
    connectedCallback() {
      this.form = this.querySelector(".store-pickup-form__form");
      this.options = this.querySelectorAll(
        ".store-pickup-form__location-input",
      );
      this.submitButton = document.querySelector(
        "button[form='store-pickup-form']",
      );
      this.dialog = this.closest("dialog");
      this.inventoryContainers = this.querySelectorAll(
        ".store-pickup-form__location-availability",
      );

      this.geolocateButton = this.querySelector(
        ".store-pickup-form__find-stores-button",
      );
      this.locationsContainer = this.querySelector(
        ".store-pickup-form__locations",
      );
      this.locations = Array.from(
        this.querySelectorAll(".store-pickup-form__location"),
      );

      let geolocationCoordinates = sessionStorage.getItem(
        "geolocation_coordinates",
      );
      if (geolocationCoordinates) {
        geolocationCoordinates = JSON.parse(geolocationCoordinates);
        this.reorderStores(
          geolocationCoordinates.lat,
          geolocationCoordinates.lng,
        );
      }

      this.initForm();
      this.bindEvents();
    }

    bindEvents() {
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
      this.geolocateButton.addEventListener(
        "click",
        this.handleGeolocation.bind(this),
      );

      this.subscribe(
        window.WYATT.PubSub.events.openStorePickupForm,
        this.renderInventory.bind(this),
      );

      this.options.forEach((option) => {
        option.addEventListener("change", this.handleOptionChange.bind(this));
      });
    }

    initForm() {
      this.savedStore = localStorage.getItem("pickup_store_id");

      if (this.savedStore) {
        this.submitButton.disabled = false;

        this.options.forEach((option) => {
          if (option.value === this.savedStore) {
            option.checked = true;
          }
        });
      }
    }

    handleSubmit(event) {
      event.preventDefault();
      const formData = new FormData(event.target);
      const storeID = formData.get("store_id");

      localStorage.setItem("pickup_store_id", storeID);
      this.dialog.close();

      // Report the confirmed store-pickup selection to GA4 (via GTM). This
      // submit handler can be bound more than once on the shared form, so guard
      // on the shared event object to emit a single event per confirmation.
      if (!event.storeSelectedReported) {
        event.storeSelectedReported = true;
        const selectedStore = getStoreData(storeID);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "store_selected",
          store_name: selectedStore ? selectedStore.name : "(unknown)",
        });
      }

      this.publish(window.WYATT.PubSub.events.updatePickupStore, {
        storeID,
      });
    }

    handleOptionChange() {
      this.submitButton.disabled = false;
    }

    async renderInventory(event) {
      const { productId, variantId, checkAllVariants } = event.detail;

      const availabilities =
        await window.WYATT.storeAvailability.gettingAvailability(productId);

      this.locations.forEach((location) => {
        const { locationId } = location.dataset;
        const inventoryContainer = location.querySelector(
          ".store-pickup-form__location-availability",
        );
        const pickupContainer = location.querySelector(
          ".store-pickup-form__location-pickup",
        );
        const freePickupMessageContainer = pickupContainer.querySelector(
          ".store-pickup-form__location-pickup-message",
        );
        const storeData = getStoreData(locationId);

        let quantityAvailable = 0;

        if (checkAllVariants) {
          Object.values(availabilities).forEach((value) => {
            const storeAvailability = value.find(
              (availability) => availability.id.toString() === locationId,
            );

            if (storeAvailability) {
              quantityAvailable += storeAvailability?.quantityAvailable || 0;
            }
          });
        } else {
          const storeAvailability = availabilities[Number(variantId)].find(
            (availability) => availability.id.toString() === locationId,
          );

          if (storeAvailability) {
            quantityAvailable = storeAvailability?.quantityAvailable;
          }
        }

        if (quantityAvailable === 1) {
          inventoryContainer.innerHTML = checkAllVariants
            ? `In Stock`
            : `${quantityAvailable} item in stock`;
        } else if (quantityAvailable > 0) {
          inventoryContainer.innerHTML = checkAllVariants
            ? `In Stock`
            : `${quantityAvailable} items in stock`;
        } else {
          inventoryContainer.innerHTML = `Out of stock`;
        }

        if (storeData.offers_store_pickup) {
          pickupContainer.dataset.available = "true";
          freePickupMessageContainer.textContent = "Free pickup available";
        } else {
          pickupContainer.dataset.available = "false";
          freePickupMessageContainer.textContent =
            "Pickup not available at this store";
        }
      });
    }

    /**
     * Geolocation
     */

    handleGeolocation(event) {
      event.preventDefault();

      // This click handler can be bound more than once on the shared button, so
      // guard on the shared event object to request the location once per click.
      if (!event.geolocationRequested) {
        event.geolocationRequested = true;

        if ("geolocation" in navigator) {
          this.dataset.loading = "true";
          navigator.geolocation.getCurrentPosition(
            this.geolocationSuccess.bind(this),
            this.geolocationError.bind(this),
          );
        } else {
          alert("Your browser does not support geolocation.");
        }
      }
    }

    geolocationSuccess(position) {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      sessionStorage.setItem(
        "geolocation_coordinates",
        JSON.stringify({ lat, lng }),
      );

      this.reorderStores(lat, lng);
      this.dataset.loading = "false";
    }

    geolocationError() {
      this.dataset.loading = "false";

      alert(
        "Unable to retrieve your location. Please check your browser location settings.",
      );
    }

    reorderStores(lat, lng) {
      const storesWithDistance = this.getStoresWithDistance(lat, lng);
      this.locationsContainer.innerHTML = "";

      storesWithDistance.forEach((store) => {
        const locationNode = this.locations.find(
          (location) => location.dataset.locationId === store.location_id,
        );

        // A store in the location data with no matching markup (a coming soon
        // store) would otherwise throw and leave the emptied list blank.
        if (!locationNode) return;

        this.locationsContainer.appendChild(locationNode);
        const distanceContainer = locationNode.querySelector(
          ".store-pickup-form__location-distance",
        );
        distanceContainer.innerHTML = `(${store.distance.toFixed(1)} mi)`;
      });
    }

    getStoresWithDistance(userLat, userLng) {
      const storesWithDistance = window.WYATT.shopify.locations
        .filter((store) => !isComingSoon(store))
        .map((store) => ({
          ...store,
          distance: this.calculateDistance(
            Number(store.latitude),
            Number(store.longitude),
            Number(userLat),
            Number(userLng),
          ),
        }));

      return storesWithDistance.sort((a, b) => a.distance - b.distance);
    }

    calculateDistance(lat1, lon1, lat2, lon2) {
      // Haversine formula
      const toRadians = (degrees) => degrees * (Math.PI / 180);

      const R = 3958.8; // Radius of Earth in miles
      const dLat = toRadians(lat2 - lat1);
      const dLon = toRadians(lon2 - lon1);

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
          Math.cos(toRadians(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      return distance;
    }
  },
);
